import React, { useEffect, useState } from "react";
import {
  Play,
  Archive,
  CheckCircle,
  MapPin,
  Truck,
  ShoppingCart,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import "./index.css";
import Swal from 'sweetalert2';
import {
  CartLoading,
  CartLoadingAllocation,
  Gate,
} from "../../../../redux/cartLoadingSlice";

interface CartsListProps {
  carts: CartLoading[];
  cartAllocations: CartLoadingAllocation[];
  gates: Gate[];
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  formatTime: (timestamp: string | null) => string;
  getTimeRemaining: (targetTime: string | null) => string;
  updateCartState: (cartId: string, status: string, gateId?: string, groupedOrderIds?: string[]) => void;
  setSelectedCart: (cartId: string) => void;
  setSelectedCartForOrderSelection: (cartId: string) => void;
  setSupervisorTab: (tab: string) => void;
  viewingCart: string;
  isDetailLoading: boolean;
  updatingCarts: Record<string, string>;
  orderSelectionLoadingCartId?: string | null;
  onRefreshCarts?: () => Promise<void>;
  isRefreshing?: boolean;
}

const CartsList: React.FC<CartsListProps> = ({
  carts,
  cartAllocations,
  gates,
  getStatusColor,
  getStatusText,
  formatTime,
  getTimeRemaining,
  updateCartState,
  setSelectedCart,
  setSelectedCartForOrderSelection,
  setSupervisorTab,
  viewingCart,
  isDetailLoading,
  updatingCarts,
  orderSelectionLoadingCartId,
  onRefreshCarts,
  isRefreshing = false,
}) => {
  const [localLoadingCartId, setLocalLoadingCartId] = useState<string | null>(null);

  // Filter out carts with no active orders
  const activeCarts = carts.filter(cart => {
    const hasActiveIndividualOrders = cart.orders && 
      cart.orders.filter(order => order.orderStatus !== "Issued by Kitchen").length > 0;
    
    const hasActiveGroupOrders = cart.groupOrders && 
      cart.groupOrders.some(group => 
        group.orders && group.orders.filter(order => order.orderStatus !== "Issued by Kitchen").length > 0
      );
    
    return hasActiveIndividualOrders || hasActiveGroupOrders;
  });

  // Debug logging
  useEffect(() => {
    activeCarts.forEach(cart => {
      const activeIndividualOrders = cart.orders?.filter(order => order.orderStatus !== "Issued by Kitchen") || [];
      const activeGroupOrders = cart.groupOrders?.filter(group => 
        group.orders?.some(order => order.orderStatus !== "Issued by Kitchen")
      ) || [];

      console.log(`Cart ${cart.displayName}:`, {
        id: cart.id,
        status: cart.status,
        activeIndividualOrdersCount: activeIndividualOrders.length,
        activeGroupOrdersCount: activeGroupOrders.length,
        hasDeliveryPerson: Boolean(cart.owner && cart.owner.id),
        shouldShowSelectButton: 
          (cart.status === "Pending" || cart.status === "CartCanLeave") && 
          activeIndividualOrders.length > 0
      });
    });
  }, [activeCarts]);

  const isButtonLoading = (cartId: string, status: string) => {
    return updatingCarts[`${cartId}-${status}`] === "processing";
  };

  const isSelectOrdersLoading = (cartId: string): boolean => {
    if (orderSelectionLoadingCartId !== undefined) {
      return orderSelectionLoadingCartId === cartId;
    }
    return localLoadingCartId === cartId;
  };

  const handleSelectOrdersAndStart = (cartId: string) => {
    if (orderSelectionLoadingCartId === undefined) {
      setLocalLoadingCartId(cartId);
      setTimeout(() => {
        setSelectedCartForOrderSelection(cartId);
        setSupervisorTab("order-selection");
        setLocalLoadingCartId(null);
      }, 300);
    } else {
      setSelectedCartForOrderSelection(cartId);
      setSupervisorTab("order-selection");
    }
  };

  const handleStartLoading = (cart: CartLoading) => {
    // Check if cart has individual orders that need grouping (excluding "Issued by Kitchen")
    const activeIndividualOrders = cart.orders?.filter(order => order.orderStatus !== "Issued by Kitchen") || [];
    const hasActiveIndividualOrders = activeIndividualOrders.length > 0;
    
    // If cart is in CartCanLeave state and has individual orders, show warning
    if (cart.status === "CartCanLeave" && hasActiveIndividualOrders) {
      Swal.fire({
        title: 'Cannot Start Loading',
        html: `This cart has <strong>${activeIndividualOrders.length} individual order(s)</strong> that need to be grouped first.<br/><br/>
               You need to create order groups before starting a new loading cycle.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Go to Order Selection',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        backdrop: true,
        allowOutsideClick: false,
      }).then((result) => {
        if (result.isConfirmed) {
          // Navigate to order selection for this cart
          handleSelectOrdersAndStart(cart.id);
        }
      });
    } else {
      // Proceed with normal start loading
      updateCartState(cart.id, "LoadingStarted");
    }
  };

  const getGroupedOrderIdsFromCart = (cart: CartLoading): string[] => {
    if (!cart.groupOrders || !Array.isArray(cart.groupOrders) || cart.groupOrders.length === 0) {
      return [];
    }
    return cart.groupOrders
      .filter(group => group && group.id)
      .map(group => group.id);
  };

  const hasCartOwner = (cart: CartLoading): boolean => {
    return Boolean(cart.owner && cart.owner.id);
  };

  const hasActiveGroupOrders = (cart: CartLoading): boolean => {
    return Boolean(cart.groupOrders && cart.groupOrders.some(group => 
      group.orders && group.orders.filter(order => order.orderStatus !== "Issued by Kitchen").length > 0
    ));
  };

  const hasActiveIndividualOrders = (cart: CartLoading): boolean => {
    return Boolean(cart.orders && cart.orders.filter(order => order.orderStatus !== "Issued by Kitchen").length > 0);
  };

  const getActiveOrdersCount = (cart: CartLoading): { individual: number; groups: number; total: number } => {
    const activeIndividualOrders = cart.orders?.filter(order => order.orderStatus !== "Issued by Kitchen") || [];
    const activeGroups = cart.groupOrders?.filter(group => 
      group.orders?.some(order => order.orderStatus !== "Issued by Kitchen")
    ) || [];
    
    const activeGroupOrdersCount = activeGroups.reduce(
      (total, groupOrder) => total + (groupOrder.orders?.filter(order => order.orderStatus !== "Issued by Kitchen").length || 0),
      0
    );

    return {
      individual: activeIndividualOrders.length,
      groups: activeGroups.length,
      total: activeIndividualOrders.length + activeGroupOrdersCount
    };
  };

  // Improved function to determine if SELECT ORDERS button should show
  const shouldShowSelectOrdersButton = (cart: CartLoading): boolean => {
    // Only show for Pending or CartCanLeave carts
    if (cart.status !== "Pending" && cart.status !== "CartCanLeave") {
      return false;
    }

    // Don't show if cart has no active individual orders
    if (!hasActiveIndividualOrders(cart)) {
      return false;
    }

    // Don't show if cart has no delivery person
    if (!hasCartOwner(cart)) {
      return false;
    }

    // Show the button! (Even if cart has group orders)
    return true;
  };

  const handleRefresh = async () => {
    if (onRefreshCarts) {
      await onRefreshCarts();
    }
  };

  // If no active carts, show empty state
  if (activeCarts.length === 0) {
    return (
      <div className="carts-list-container">
        <div className="carts-list-header">
          <h3 className="carts-list-title">Cart Loading Queue</h3>
          {onRefreshCarts && (
            <button
              onClick={handleRefresh}
              className="refresh-button"
              disabled={isRefreshing}
              title="Refresh cart list"
            >
              <RefreshCw className={`refresh-icon ${isRefreshing ? 'refresh-spinning' : ''}`} />
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </button>
          )}
        </div>
        <div className="no-carts-message">
          <div className="no-carts-icon">
            <span>🛒</span>
          </div>
          <h3>No Active Carts</h3>
          <p>All orders have been marked as "Issued by Kitchen".</p>
        </div>
      </div>
    );
  }

  return (
    <div className="carts-list-container">
      <div className="carts-list-header">
        <h3 className="carts-list-title">Cart Loading Queue</h3>
        {onRefreshCarts && (
          <button
            onClick={handleRefresh}
            className="refresh-button"
            disabled={isRefreshing}
            title="Refresh cart list"
          >
            <RefreshCw className={`refresh-icon ${isRefreshing ? 'refresh-spinning' : ''}`} />
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </button>
        )}
      </div>

      <div className="carts-grid">
        {activeCarts.map((cart) => {
          const assignedGate = gates.find((g) => g.id === cart.gateId);
          const cartHasActiveGroupOrders = hasActiveGroupOrders(cart);
          const cartHasActiveIndividualOrders = hasActiveIndividualOrders(cart);
          const activeOrders = getActiveOrdersCount(cart);
          const hasDeliveryPerson = hasCartOwner(cart);
          const showSelectOrders = shouldShowSelectOrdersButton(cart);

          return (
            <div
              key={cart.id}
              className={`cart-card ${!hasDeliveryPerson ? 'cart-card-disabled' : ''} ${
                cart.status === "CartCanLeave" && cartHasActiveIndividualOrders && !cartHasActiveGroupOrders ? 'cart-card-needs-grouping' : ''
              }`}
            >
              <div className="cart-card-header">
                <div className="cart-card-info">
                  <h4 className="cart-card-id">{cart.displayName}</h4>
                  <p className="cart-card-rider">{cart.rider}</p>
                </div>
                <span className={`cart-status-badge ${getStatusColor(cart.status)}`}>
                  {getStatusText(cart.status)}
                </span>
              </div>

              <div className="cart-card-details">
                <div className="cart-card-detail">
                  <span className="cart-card-detail-label">Distance:</span>
                  <span className="cart-card-detail-value">
                    {cart.distance?.toFixed(2) || 0} km
                  </span>
                </div>
                <div className="cart-card-detail">
                  <span className="cart-card-detail-label">Gate:</span>
                  <span className="cart-card-detail-value">
                    {assignedGate?.name || "Not Assigned"}
                  </span>
                </div>
                {cart.loadingStartedAt && (
                  <div className="cart-card-detail">
                    <span className="cart-card-detail-label">Started:</span>
                    <span className="cart-card-detail-value">
                      {formatTime(cart.loadingStartedAt)}
                    </span>
                  </div>
                )}
                {cart.estimatedLeaveTime && (
                  <div className="cart-card-detail">
                    <span className="cart-card-detail-label">ETD:</span>
                    <span
                      className={`cart-card-detail-value ${
                        getTimeRemaining(cart.estimatedLeaveTime) === "Overdue"
                          ? "cart-eta-overdue"
                          : "cart-eta-normal"
                      }`}
                    >
                      {getTimeRemaining(cart.estimatedLeaveTime)}
                    </span>
                  </div>
                )}
                {activeOrders.total > 0 && (
                  <div className="cart-card-detail">
                    <span className="cart-card-detail-label">Active Orders:</span>
                    <span className="cart-card-detail-value">
                      {activeOrders.total} orders
                      {(cartHasActiveIndividualOrders || cartHasActiveGroupOrders) && (
                        <span className="cart-order-breakdown">
                          {" "}
                          ({activeOrders.individual} individual, {activeOrders.groups} groups)
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              {!hasDeliveryPerson && (
                <div className="cart-warning-message">
                  <AlertTriangle size={14} className="cart-warning-icon" />
                  <span>This cart is not assigned to a delivery person</span>
                </div>
              )}

              <div className="cart-card-actions">
              
                {/* START LOADING button - For Pending carts with no individual orders, OR for CartCanLeave carts */}
                {(cart.status === "Pending" || cart.status === "CartCanLeave") && 
                 !cartHasActiveIndividualOrders && 
                 hasDeliveryPerson && (
                  <button
                    onClick={() => handleStartLoading(cart)}
                    className="cart-action-button cart-action-button-primary"
                    disabled={isButtonLoading(cart.id, "LoadingStarted")}
                    title={!hasDeliveryPerson ? "Cart not assigned to delivery person" : ""}
                  >
                    {isButtonLoading(cart.id, "LoadingStarted") ? (
                      "⏳ Processing..."
                    ) : (
                      <>
                        <Play className="cart-action-icon" />
                        🚀 START NEW CYCLE
                      </>
                    )}
                  </button>
                )}

                {/* SELECT ORDERS button - Show when there are individual orders, regardless of group orders */}
                {showSelectOrders && (
                  <button
                    onClick={() => handleSelectOrdersAndStart(cart.id)}
                    className="cart-action-button cart-action-button-secondary"
                    disabled={isSelectOrdersLoading(cart.id)}
                  >
                    {isSelectOrdersLoading(cart.id) ? (
                      <>
                        <span className="cart-action-spinner" />
                        ⏳ Loading...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="cart-action-icon" />
                        📋 SELECT ORDERS & START NEW CYCLE
                      </>
                    )}
                  </button>
                )}

                {/* Regular workflow buttons for other statuses */}
                {cart.status === "LoadingStarted" && (
                  <button
                    onClick={() => updateCartState(cart.id, "CratesCollected")}
                    className="cart-action-button cart-action-button-secondary"
                    disabled={isButtonLoading(cart.id, "CratesCollected") || !hasDeliveryPerson}
                    title={!hasDeliveryPerson ? "Cart not assigned to delivery person" : ""}
                  >
                    {isButtonLoading(cart.id, "CratesCollected") ? (
                      "⏳ Processing..."
                    ) : (
                      <>
                        <Archive className="cart-action-icon" />
                        Mark Crates Collected (TL)
                      </>
                    )}
                  </button>
                )}

                {cart.status === "CratesCollected" && (
                  <button
                    onClick={() => updateCartState(cart.id, "TLItemsVerification")}
                    className="cart-action-button cart-action-button-warning"
                    disabled={isButtonLoading(cart.id, "TLItemsVerification") || !hasDeliveryPerson}
                    title={!hasDeliveryPerson ? "Cart not assigned to delivery person" : ""}
                  >
                    {isButtonLoading(cart.id, "TLItemsVerification") ? (
                      "⏳ Processing..."
                    ) : (
                      <>
                        <CheckCircle className="cart-action-icon" />
                        Mark TL Items Verification
                      </>
                    )}
                  </button>
                )}

                {cart.status === "TLItemsVerification" && (
                  <button
                    onClick={() => updateCartState(cart.id, "CartPersonVerification")}
                    className="cart-action-button cart-action-button-purple"
                    disabled={isButtonLoading(cart.id, "CartPersonVerification") || !hasDeliveryPerson}
                    title={!hasDeliveryPerson ? "Cart not assigned to delivery person" : ""}
                  >
                    {isButtonLoading(cart.id, "CartPersonVerification") ? (
                      "⏳ Processing..."
                    ) : (
                      <>
                        <CheckCircle className="cart-action-icon" />
                        Mark Cart Person Verification
                      </>
                    )}
                  </button>
                )}

                {cart.status === "CartPersonVerification" && (
                  <div className="cart-gate-assignment">
                    <div className="cart-gate-ready">
                      <p className="cart-gate-ready-text">
                        ✅ Cart Person Verified - Ready for Gate Assignment
                      </p>
                    </div>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          updateCartState(cart.id, "GateAssigned", e.target.value);
                        }
                      }}
                      className="cart-gate-select"
                      value=""
                      disabled={!hasDeliveryPerson}
                      title={!hasDeliveryPerson ? "Cart not assigned to delivery person" : ""}
                    >
                      <option value="">🚪 Select Gate...</option>
                      {gates.map((gate) => (
                        <option key={gate.id} value={gate.id}>
                          📍 {gate.name}{" "}
                          {gate.id === cart.gateId ? "(Current)" : ""}
                        </option>
                      ))}
                    </select>
                    {cart.gateId && (
                      <p className="cart-current-gate-info">
                        Current gate:{" "}
                        {gates.find((g) => g.id === cart.gateId)?.name}
                      </p>
                    )}
                  </div>
                )}

                {cart.status === "GateAssigned" && (
                  <button
                    onClick={() => updateCartState(cart.id, "AtGate")}
                    className="cart-action-button cart-action-button-purple"
                    disabled={isButtonLoading(cart.id, "AtGate") || !hasDeliveryPerson}
                    title={!hasDeliveryPerson ? "Cart not assigned to delivery person" : ""}
                  >
                    {isButtonLoading(cart.id, "AtGate") ? (
                      "⏳ Processing..."
                    ) : (
                      <>
                        <MapPin className="cart-action-icon" />
                        Mark Arrived at Gate (Cart Person)
                      </>
                    )}
                  </button>
                )}

                {cart.status === "AtGate" && (
                  <button
                    onClick={() => {
                      const groupedOrderIds = getGroupedOrderIdsFromCart(cart);
                      updateCartState(cart.id, "CartCanLeave", undefined, groupedOrderIds);
                    }}
                    className="cart-action-button cart-action-button-success"
                    disabled={isButtonLoading(cart.id, "CartCanLeave") || !hasDeliveryPerson}
                    title={!hasDeliveryPerson ? "Cart not assigned to delivery person" : ""}
                  >
                    {isButtonLoading(cart.id, "CartCanLeave") ? (
                      "⏳ Processing..."
                    ) : (
                      <>
                        <Truck className="cart-action-icon" />
                        Mark Cart Can Leave
                      </>
                    )}
                  </button>
                )}

                {/* View Details button for in-progress carts */}
                {cart.status !== "Pending" && cart.status !== "CartCanLeave" && (
                  <button
                    onClick={() => {
                      setSelectedCart(cart.id);
                      setSupervisorTab("loading");
                    }}
                    className="cart-action-button cart-action-button-view-details"
                    disabled={(isDetailLoading && viewingCart === cart.id) || !hasDeliveryPerson}
                    title={!hasDeliveryPerson ? "Cart not assigned to delivery person" : ""}
                  >
                    {isDetailLoading && viewingCart === cart.id
                      ? "⏳ Viewing..."
                      : "📊 View Details"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CartsList;